/**
 * Run `callback` once the page has loaded and the main thread is idle.
 *
 * Two details matter for Core Web Vitals:
 *
 * 1. It waits for `load` *before* asking for idle time. `requestIdleCallback`
 *    on its own can fire during a lull earlier in the page lifecycle and pull
 *    deferred work back into the LCP window — which defeats the point.
 * 2. It falls back to a plain timeout where `requestIdleCallback` is missing,
 *    and always honours `timeout` so the work can't be starved indefinitely on
 *    a busy thread.
 *
 * Returns a cleanup function that cancels whatever is still outstanding.
 */
export function afterLoadWhenIdle(callback: () => void, timeout = 3000): () => void {
  let idleHandle: number | undefined
  let timeoutHandle: number | undefined

  const schedule = () => {
    // Feature-test the function itself rather than `'requestIdleCallback' in
    // window`: the property is declared on Window in lib.dom, so TypeScript
    // narrows the negative branch of an `in` check to `never`.
    if (typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(callback, { timeout })
    } else {
      timeoutHandle = window.setTimeout(callback, Math.min(timeout, 1500))
    }
  }

  if (document.readyState === 'complete') schedule()
  else window.addEventListener('load', schedule, { once: true })

  return () => {
    window.removeEventListener('load', schedule)
    if (idleHandle !== undefined && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleHandle)
    }
    if (timeoutHandle !== undefined) window.clearTimeout(timeoutHandle)
  }
}
